import {useMemo} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'

import {Hero, Grid, BreakpointSize, useWindowSize, Text, InlineLink} from '@primer/react-brand'

import {Hero as HeroNext} from '@primer/react-brand-next'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {useRoutePayload} from '@github-ui/react-core/use-route-payload'

import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import type {GenericContent, GenericSectionWithIds} from '../../../../brand/lib/types/contentful'

import {cohortFunnelBuilder} from '../../../../lib/analytics'

type Props = {
  contentfulContent: GenericSectionWithIds
}

export function HeroSection(props: Props) {
  const isPrimerBrandNextEnabled = isFeatureEnabled('primer_brand_next')
  const {contentfulContent} = props

  const {featuresCopilotAiCodeEditorHero} = contentfulContent.ids as {
    featuresCopilotAiCodeEditorHero: GenericContent
  }

  const {heading, subheading, links, ctas, content, media} = featuresCopilotAiCodeEditorHero?.fields || {}

  // Extract hero image from media
  const heroImage = media?.[0]

  const defaultCtas = ctas?.find(ctaGroup => ctaGroup.fields.condition === 'None')
  const primaryCta = defaultCtas?.fields.primary || links?.at(0)
  const secondaryCta = defaultCtas?.fields.secondary || links?.at(1)

  const tertiaryCta = content?.find(item => item.fields.id === 'featuresCopilotAiCodeEditorHeroTertiaryCta') as
    | GenericContent
    | undefined

  const {cft} = useRoutePayload<{cft: string}>()
  const withCft = cohortFunnelBuilder(cft)

  const windowSize = useWindowSize()
  const isDesktopView = useMemo(
    () =>
      [BreakpointSize.MEDIUM, BreakpointSize.LARGE, BreakpointSize.XLARGE, BreakpointSize.XXLARGE].includes(
        windowSize.currentBreakpointSize!,
      ),
    [windowSize.currentBreakpointSize],
  )

  return (
    <Grid as="section" className="position-relative">
      <Grid.Column span={12}>
        {featuresCopilotAiCodeEditorHero ? (
          isPrimerBrandNextEnabled ? (
            <HeroNext align="center" className="pr-pb-4">
              <HeroNext.Heading size="2" data-hpc>
                {heading}
              </HeroNext.Heading>

              {subheading ? <HeroNext.Description>{documentToPlainTextString(subheading)}</HeroNext.Description> : null}

              <div className="lp-Hero-ctaButtons">
                {primaryCta && (
                  <HeroNext.PrimaryAction
                    href={primaryCta.fields.href}
                    hasArrow={false}
                    {...getAnalyticsEvent({
                      action: primaryCta.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero',
                    })}
                  >
                    {primaryCta.fields.text}
                  </HeroNext.PrimaryAction>
                )}

                {secondaryCta ? (
                  <HeroNext.SecondaryAction
                    href={withCft(secondaryCta.fields.href)}
                    hasArrow={false}
                    variant="subtle"
                    {...getAnalyticsEvent({
                      action: secondaryCta.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero',
                    })}
                  >
                    {secondaryCta.fields.text}
                  </HeroNext.SecondaryAction>
                ) : null}
              </div>

              {isDesktopView && tertiaryCta && tertiaryCta.fields.text ? (
                <Text weight="normal" style={{marginTop: '1rem'}}>
                  {documentToReactComponents(tertiaryCta.fields.text, {
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (_, children) => [children],
                      [INLINES.HYPERLINK]: (node, children) => (
                        <InlineLink
                          href={node.data.uri}
                          className="tertiary-cta"
                          {...getAnalyticsEvent({
                            action: documentToPlainTextString(node, ' '),
                            tag: 'link',
                            context: 'CTAs',
                            location: 'hero',
                          })}
                        >
                          {children}
                        </InlineLink>
                      ),
                    },
                  })}
                </Text>
              ) : null}
            </HeroNext>
          ) : (
            <Hero align="center" className="pr-pb-4">
              <Hero.Heading size="2" data-hpc>
                {heading}
              </Hero.Heading>

              {subheading ? (
                <Hero.Description variant="muted" style={{marginTop: '1rem'}}>
                  {documentToPlainTextString(subheading)}
                </Hero.Description>
              ) : null}

              <div className="lp-Hero-ctaButtons">
                {primaryCta && (
                  <Hero.PrimaryAction
                    href={primaryCta.fields.href}
                    hasArrow={false}
                    {...getAnalyticsEvent({
                      action: primaryCta.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero',
                    })}
                  >
                    {primaryCta.fields.text}
                  </Hero.PrimaryAction>
                )}

                {secondaryCta ? (
                  <Hero.SecondaryAction
                    href={withCft(secondaryCta.fields.href)}
                    hasArrow={false}
                    variant="subtle"
                    {...getAnalyticsEvent({
                      action: secondaryCta.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero',
                    })}
                  >
                    {secondaryCta.fields.text}
                  </Hero.SecondaryAction>
                ) : null}
              </div>

              {isDesktopView && tertiaryCta && tertiaryCta.fields.text ? (
                <Text weight="normal" style={{marginTop: '1rem'}}>
                  {documentToReactComponents(tertiaryCta.fields.text, {
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (_, children) => [children],
                      [INLINES.HYPERLINK]: (node, children) => (
                        <InlineLink
                          href={node.data.uri}
                          className="tertiary-cta"
                          {...getAnalyticsEvent({
                            action: documentToPlainTextString(node, ' '),
                            tag: 'link',
                            context: 'CTAs',
                            location: 'hero',
                          })}
                        >
                          {children}
                        </InlineLink>
                      ),
                    },
                  })}
                </Text>
              ) : null}
            </Hero>
          )
        ) : null}

        <div className="pr-mt-6 pr-mt-md-8">
          {heroImage ? (
            <img
              src={heroImage.fields.asset.fields.file.url}
              alt={heroImage.fields.description || heroImage.fields.asset.fields.description || ''}
              style={{width: '100%', height: 'auto'}}
            />
          ) : null}
        </div>
      </Grid.Column>
    </Grid>
  )
}
